/**
 * Sodium Club — AJAX cart + drawer controller.
 *
 * Progressive enhancement: without JS every add-to-cart form still posts
 * natively to /cart/add and every cart trigger navigates to /cart.
 *
 * Public API:
 *   window.SodiumCart = { open, refresh, add, change }
 *     open()                  — opens the cart drawer (refreshes content once per page load)
 *     refresh()               — re-renders drawer + counts, dispatches 'sc:cart:updated', resolves with cart JSON
 *     add(formDataOrObject)   — POST /cart/add.js, then re-render; resolves with cart JSON
 *     change(line, quantity)  — POST /cart/change.js (1-based line index), then re-render; resolves with cart JSON
 *
 * Event:
 *   document CustomEvent 'sc:cart:updated' — detail is the cart JSON, fired after every mutation.
 *
 * DOM hooks:
 *   [data-cart-open]                     — click opens the drawer (native href kept as no-JS fallback)
 *   [data-cart-count]                    — item-count badges, kept in sync (hidden attr when 0)
 *   [data-cart-drawer-content]           — drawer content, swapped via the Section Rendering API
 *   [data-cart-qty][data-line][data-quantity] — quantity/remove buttons inside the drawer
 */
(function () {
  'use strict';

  var config = window.SodiumClub || {};
  var cartUrl = config.cartUrl || '/cart';
  var addUrl = (config.cartAddUrl || '/cart/add') + '.js';
  var changeUrl = (config.cartChangeUrl || '/cart/change') + '.js';

  var SECTION_ID = 'cart-drawer';
  var LOADING_LABEL = 'Wird hinzugefügt…';
  var GENERIC_ERROR = 'Das hat leider nicht geklappt. Bitte versuch es noch einmal.';

  var drawerRefreshed = false;

  function getDrawer() {
    return document.getElementById('sc-cart-drawer');
  }

  function getContent() {
    return document.querySelector('[data-cart-drawer-content]');
  }

  function dispatchUpdated(cart) {
    document.dispatchEvent(new CustomEvent('sc:cart:updated', { detail: cart }));
  }

  function updateCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (element) {
      element.textContent = count;
      if (count > 0) {
        element.removeAttribute('hidden');
      } else {
        element.setAttribute('hidden', '');
      }
    });
  }

  function setBusy(busy) {
    var content = getContent();
    if (!content) return;
    content.classList.toggle('is-busy', busy);
    content.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function fetchCart() {
    return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  function renderDrawer() {
    var content = getContent();
    if (!content) return Promise.resolve();

    return fetch(cartUrl + '?sections=' + SECTION_ID)
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var html = sections[SECTION_ID];
        if (!html) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var next = parsed.querySelector('[data-cart-drawer-content]');
        if (!next) return;

        content.innerHTML = next.innerHTML;
        content.setAttribute('data-item-count', next.getAttribute('data-item-count') || '0');
        drawerRefreshed = true;
        updateCounts(parseInt(content.getAttribute('data-item-count'), 10) || 0);
      });
  }

  function refresh() {
    setBusy(true);
    return Promise.all([renderDrawer(), fetchCart()])
      .then(function (results) {
        var cart = results[1];
        dispatchUpdated(cart);
        return cart;
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function open() {
    var drawer = getDrawer();
    if (!drawer || typeof drawer.showModal !== 'function') return;

    if (!drawer.open) drawer.showModal();

    if (!drawerRefreshed) {
      setBusy(true);
      renderDrawer()
        .catch(function () {})
        .then(function () {
          setBusy(false);
        });
    }
  }

  function parseJsonResponse(response, fallbackMessage) {
    return response
      .json()
      .catch(function () {
        return {};
      })
      .then(function (json) {
        if (!response.ok) {
          var error = new Error(json.description || json.message || fallbackMessage);
          error.status = response.status;
          throw error;
        }
        return json;
      });
  }

  function add(data) {
    var body;
    if (data instanceof FormData) {
      body = data;
    } else {
      body = new FormData();
      body.append('id', data.id);
      body.append('quantity', data.quantity || 1);
    }

    return fetch(addUrl, {
      method: 'POST',
      body: body,
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return parseJsonResponse(response, GENERIC_ERROR);
      })
      .then(function () {
        return Promise.all([renderDrawer(), fetchCart()]);
      })
      .then(function (results) {
        var cart = results[1];
        dispatchUpdated(cart);
        return cart;
      });
  }

  function change(line, quantity) {
    setBusy(true);
    return fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (response) {
        return parseJsonResponse(response, GENERIC_ERROR);
      })
      .then(function (cart) {
        return renderDrawer().then(function () {
          dispatchUpdated(cart);
          return cart;
        });
      })
      .finally(function () {
        setBusy(false);
      });
  }

  /* ---- Submit interception (add-to-cart forms) ---- */

  function startLoading(button) {
    if (!button) return function () {};

    var originalHtml = button.innerHTML;
    var wasDisabled = button.disabled;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = LOADING_LABEL;

    return function () {
      button.disabled = wasDisabled;
      button.removeAttribute('aria-busy');
      button.innerHTML = originalHtml;
    };
  }

  function clearError(form) {
    var error = form.querySelector('[data-cart-error]');
    if (error) error.remove();
  }

  function showError(form, message) {
    clearError(form);
    var error = document.createElement('p');
    error.className = 'sc-cart-error';
    error.setAttribute('data-cart-error', '');
    error.setAttribute('role', 'alert');
    error.textContent = message || GENERIC_ERROR;
    form.appendChild(error);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    // Do not intercept dynamic checkout / payment buttons — only the regular
    // add button (name="add") or submits without an explicit submitter.
    var submitter = event.submitter;
    if (submitter && submitter.name !== 'add') return;

    if (!getDrawer()) return; // no drawer on this page — keep native POST

    event.preventDefault();

    var button = submitter || form.querySelector('button[name="add"], [type="submit"]');
    var stopLoading = startLoading(button);
    clearError(form);

    add(new FormData(form))
      .then(function () {
        stopLoading();
        open();
      })
      .catch(function (error) {
        stopLoading();
        showError(form, error && error.message);
      });
  });

  /* ---- Click delegation: drawer triggers + quantity steppers ---- */

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-cart-open]');
    if (opener) {
      if (!getDrawer()) return; // no drawer — let the link navigate to /cart
      event.preventDefault();
      open();
      return;
    }

    var qtyButton = event.target.closest('[data-cart-qty]');
    if (qtyButton) {
      var line = parseInt(qtyButton.getAttribute('data-line'), 10);
      var quantity = parseInt(qtyButton.getAttribute('data-quantity'), 10);
      if (!isNaN(line) && !isNaN(quantity) && quantity >= 0) {
        change(line, quantity).catch(function () {});
      }
    }
  });

  window.SodiumCart = {
    open: open,
    refresh: refresh,
    add: add,
    change: change
  };
})();
